import type { ReactNode } from 'react';
import { fasseZusammen, fussArt, fussWert, type FussDefinition, type Fusswert } from './tabellengruppen';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BAUM - die rechnende Hälfte (TX3, TX-STANDARD S1.3, Ausnahme §3.4)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Gemeinsam für `Baum` (Gliederung links, Auswahl → Tafel rechts) und
 * `BaumTabelle` (Netz als Tabelle mit Ebenen, z. B. Strang → Teilstrecke).
 * Ohne React, deshalb einzeln prüfbar.
 *
 * WAS DER BESTAND TAT (Befund TX §3.3, `HeizungRohrnetz.tsx:307`)
 * ------------------------------------------------------------------
 * Vier Ebenen von Hand (Gruppe → Klasse → Weg → Teilstrecke), je Ebene ein
 * eigener Zustand, `wege.slice(0, 200)` mit „200 von 600 gezeigt" - die
 * übrigen Wege waren nicht erreichbar. Hier steht der Baum als FLACHE
 * Sichtliste: Nur aufgeklappte Knoten haben Kinder darin, und die Liste lässt
 * sich wie eine Tabelle virtualisieren. Kein Knoten fällt weg.
 */

export interface BaumKnoten<D = unknown> {
  /** Eindeutig im ganzen Baum (nicht nur unter den Geschwistern). */
  id: string;
  /** Klartext des Knotens - für die Anzeige, Vorlesehilfen und die Suche. */
  titel: string;
  /** Fachdaten (für `BaumTabelle` die Zeile). */
  daten?: D;
  /** Bekannte Kinder. `undefined` + `hatKinder` = nachzuladen. */
  kinder?: BaumKnoten<D>[];
  /** Hat Kinder, die erst beim Aufklappen geladen werden. */
  hatKinder?: boolean;
  /** Zahl der Kinder, die der Knoten hat (sichtbar, auch bevor sie geladen sind). */
  anzahl?: number;
  /** Kennwert rechts, z. B. `<WertEinheit …/>`. */
  wert?: ReactNode;
  /** Fachstatus, z. B. `<StatusBadge … klein />`. */
  marke?: ReactNode;
  /** Kurzer sichtbarer Zusatz (z. B. „maßgebend"). Kein Tooltip. */
  hinweis?: string;
}

/** Ladezustand der nachgeladenen Kinder eines Knotens. */
export type Kinderzustand<D> =
  | { zustand: 'laedt' }
  | { zustand: 'fehler'; fehler: unknown }
  | { zustand: 'geladen'; kinder: BaumKnoten<D>[] };

/** Eine Zeile der Sichtliste. */
export type Baumzeile<D> =
  | {
    art: 'knoten';
    knoten: BaumKnoten<D>;
    ebene: number; // 1 = Wurzel (aria-level)
    position: number; // 1-basiert unter den Geschwistern (aria-posinset)
    geschwister: number; // aria-setsize
    eltern: string | null;
    pfad: string[]; // Ids von der Wurzel bis einschließlich dieses Knotens
    aufklappbar: boolean;
    offen: boolean;
  }
  | { art: 'laedt' | 'leer'; eltern: string; ebene: number }
  | { art: 'fehler'; eltern: string; ebene: number; fehler: unknown };

/** Die Kinder eines Knotens, so weit bekannt: eigene, sonst nachgeladene. */
export function kinderVon<D>(
  k: BaumKnoten<D>,
  geladen: ReadonlyMap<string, Kinderzustand<D>>,
): BaumKnoten<D>[] | undefined {
  if (k.kinder) return k.kinder;
  const z = geladen.get(k.id);
  return z?.zustand === 'geladen' ? z.kinder : undefined;
}

export function istAufklappbar<D>(k: BaumKnoten<D>): boolean {
  return (k.kinder !== undefined && k.kinder.length > 0) || (k.kinder === undefined && k.hatKinder === true);
}

/**
 * Die flache Sichtliste: jeder Knoten, dessen Vorfahren alle offen sind, in
 * Baumreihenfolge. Unter einem offenen Knoten, dessen Kinder noch laden, nicht
 * geladen werden konnten oder leer sind, steht eine Zustandszeile.
 */
export function sichtliste<D>(
  wurzeln: readonly BaumKnoten<D>[],
  istOffen: (id: string) => boolean,
  geladen: ReadonlyMap<string, Kinderzustand<D>> = new Map(),
): Baumzeile<D>[] {
  const aus: Baumzeile<D>[] = [];

  const besuche = (liste: readonly BaumKnoten<D>[], ebene: number, eltern: string | null, pfad: string[]) => {
    liste.forEach((k, i) => {
      const aufklappbar = istAufklappbar(k);
      const offen = aufklappbar && istOffen(k.id);
      const eigenerPfad = [...pfad, k.id];
      aus.push({
        art: 'knoten', knoten: k, ebene, position: i + 1, geschwister: liste.length,
        eltern, pfad: eigenerPfad, aufklappbar, offen,
      });
      if (!offen) return;

      const kinder = kinderVon(k, geladen);
      if (kinder) {
        if (kinder.length === 0) aus.push({ art: 'leer', eltern: k.id, ebene: ebene + 1 });
        else besuche(kinder, ebene + 1, k.id, eigenerPfad);
        return;
      }
      const z = geladen.get(k.id);
      if (z?.zustand === 'fehler') aus.push({ art: 'fehler', eltern: k.id, ebene: ebene + 1, fehler: z.fehler });
      else aus.push({ art: 'laedt', eltern: k.id, ebene: ebene + 1 });
    });
  };

  besuche(wurzeln, 1, null, []);
  return aus;
}

/** Was eine Taste im Baum bewirkt (WAI-ARIA Authoring Practices, „Tree View"). */
export type Baumhandlung =
  | { art: 'fokus'; index: number }
  | { art: 'oeffnen'; id: string }
  | { art: 'schliessen'; id: string }
  | { art: 'waehlen'; index: number }
  | { art: 'nichts' };

/**
 * Tastatur im Baum. `index` ist die Lage des fokussierten KNOTENS in der
 * Sichtliste; Zustandszeilen werden übersprungen.
 *
 * - ↓/↑: nächster/vorheriger sichtbarer Knoten;
 * - →: zu → aufklappen; offen → erstes Kind;
 * - ←: offen → zuklappen; sonst → Elternknoten;
 * - Pos1/Ende: erster/letzter sichtbarer Knoten;
 * - Eingabe/Leertaste: wählen.
 */
export function baumTaste<D>(zeilen: readonly Baumzeile<D>[], index: number, taste: string): Baumhandlung {
  const z = zeilen[index];
  if (!z || z.art !== 'knoten') return { art: 'nichts' };
  const knotenAb = (start: number, schritt: 1 | -1) => {
    for (let i = start; i >= 0 && i < zeilen.length; i += schritt) {
      if (zeilen[i].art === 'knoten') return i;
    }
    return -1;
  };

  switch (taste) {
    case 'ArrowDown': {
      const i = knotenAb(index + 1, 1);
      return i < 0 ? { art: 'nichts' } : { art: 'fokus', index: i };
    }
    case 'ArrowUp': {
      const i = knotenAb(index - 1, -1);
      return i < 0 ? { art: 'nichts' } : { art: 'fokus', index: i };
    }
    case 'ArrowRight': {
      if (!z.aufklappbar) return { art: 'nichts' };
      if (!z.offen) return { art: 'oeffnen', id: z.knoten.id };
      const naechste = zeilen[index + 1];
      return naechste?.art === 'knoten' && naechste.eltern === z.knoten.id
        ? { art: 'fokus', index: index + 1 } : { art: 'nichts' };
    }
    case 'ArrowLeft': {
      if (z.offen) return { art: 'schliessen', id: z.knoten.id };
      if (z.eltern === null) return { art: 'nichts' };
      const i = zeilen.findIndex((x) => x.art === 'knoten' && x.knoten.id === z.eltern);
      return i < 0 ? { art: 'nichts' } : { art: 'fokus', index: i };
    }
    case 'Home': {
      const i = knotenAb(0, 1);
      return i < 0 ? { art: 'nichts' } : { art: 'fokus', index: i };
    }
    case 'End': {
      const i = knotenAb(zeilen.length - 1, -1);
      return i < 0 ? { art: 'nichts' } : { art: 'fokus', index: i };
    }
    case 'Enter':
    case ' ':
      return { art: 'waehlen', index };
    default:
      return { art: 'nichts' };
  }
}

/** Die Ids aller Vorfahren eines Knotens (zum Aufklappen bis zu einer Auswahl). */
export function vorfahren<D>(wurzeln: readonly BaumKnoten<D>[], id: string,
  geladen: ReadonlyMap<string, Kinderzustand<D>> = new Map()): string[] | null {
  const suche = (liste: readonly BaumKnoten<D>[], pfad: string[]): string[] | null => {
    for (const k of liste) {
      if (k.id === id) return pfad;
      const kinder = kinderVon(k, geladen);
      if (kinder) {
        const r = suche(kinder, [...pfad, k.id]);
        if (r) return r;
      }
    }
    return null;
  };
  return suche(wurzeln, []);
}

/**
 * Teilsumme eines Knotens in der `BaumTabelle`: über die BLÄTTER darunter,
 * nach der Fehlstellenregel. Ein innerer Knoten mit eigenem Wert (z. B. vom
 * Rechenkern) zeigt den eigenen Wert, nicht die Teilsumme.
 *
 * Sind Kinder noch nicht geladen, ist die Teilsumme nicht bekannt - dann
 * `null` und nicht „0" (S0.4).
 */
export function teilsumme<D>(
  k: BaumKnoten<D>,
  wert: (d: D) => unknown,
  definition: FussDefinition,
  geladen: ReadonlyMap<string, Kinderzustand<D>> = new Map(),
): Fusswert | null {
  const art = fussArt(definition);
  if (art === null) return null;
  const blaetter: unknown[] = [];
  let unbekannt = false;
  const sammle = (x: BaumKnoten<D>) => {
    const kinder = kinderVon(x, geladen);
    if (kinder === undefined && x.hatKinder) { unbekannt = true; return; }
    if (!kinder || kinder.length === 0) { blaetter.push(x.daten === undefined ? null : wert(x.daten)); return; }
    kinder.forEach(sammle);
  };
  sammle(k);
  if (unbekannt) return null;
  const schranke = typeof definition === 'object' && 'schranke' in definition ? definition.schranke ?? false : false;
  return art === 'anzahl'
    ? fussWert('anzahl', blaetter)
    : fasseZusammen(blaetter, art, schranke);
}
